import type { TFunction } from 'i18next';
import type { HmsApprovalMode, RiskMatrixChange, RiskMatrixFactor, RiskMatrixSettings } from '../../../api/hms';
import { approvalCommentMissing, approvalCommentRequired } from '../hmsApproval';

/*
 * Validation and the change list are also done here, so the form responds while HR edits.
 * The API validates again and is the source of truth. The tests mirror the API tests.
 */

export const SEXUAL_HARASSMENT = 'SEXUAL_HARASSMENT';
/** The order in «Én melding gir høy risiko». Sexual harassment is always on. */
export const ONE_REPORT_KEYS = [SEXUAL_HARASSMENT, 'HARASSMENT', 'VIOLENCE_THREATS'] as const;

const LEVELS = [1, 2, 3, 4, 5];

export type RiskLevel = 'low' | 'moderate' | 'high';

export const riskLevel = (score: number, s: Pick<RiskMatrixSettings, 'moderateFrom' | 'highFrom'>): RiskLevel =>
  score >= s.highFrom ? 'high' : score >= s.moderateFrom ? 'moderate' : 'low';

/** Highest likelihood first */
export const matrixRows = () =>
  [...LEVELS].reverse().map((likelihood) => ({
    likelihood,
    cells: LEVELS.map((consequence) => ({ consequence, score: likelihood * consequence })),
  }));

export const cellsWithLevel = (level: RiskLevel, s: RiskMatrixSettings) =>
  matrixRows()
    .flatMap((row) => row.cells)
    .filter((cell) => riskLevel(cell.score, s) === level).length;

export const thresholdsInvalid = (s: RiskMatrixSettings) => s.highFrom <= s.moderateFrom;

const bandInRange = (band: number) => Number.isInteger(band) && band >= 1 && band <= 99;

/** Whether a limit breaks the rule that limits increase from 1 to 99 % */
export const bandInvalid = (bands: number[], index: number) =>
  !bandInRange(bands[index]) || (index > 0 && bands[index] <= bands[index - 1]);

export const bandsInvalid = (bands: number[]) => bands.some((_, index) => bandInvalid(bands, index));

export const likelihoodRanges = (bands: number[]) =>
  LEVELS.map((level, index) => ({
    level,
    from: index === 0 ? 0 : bands[index - 1] + 1,
    to: index < 4 ? bands[index] : 100,
  }));

/** The links are shown to employees who may report something serious, so they must be secure */
const isHttpsUrl = (value: string) => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

export type UrlError = 'invalid' | null;

/** Both links are optional. Without them the survey tells the employees to use the organisation's own routines. */
export const linkError = (value: string | null): UrlError =>
  value?.trim() && !isHttpsUrl(value.trim()) ? 'invalid' : null;

type FactorConsequence = Pick<RiskMatrixFactor, 'key' | 'consequence'>;

export const consequenceOf = (s: RiskMatrixSettings, factor: FactorConsequence) =>
  s.consequenceOverrides[factor.key] ?? factor.consequence;

/** The lowest consequence where one report, which gives likelihood 5, is also high in the matrix */
export const oneReportMinConsequence = (highFrom: number) => Math.ceil(highFrom / 5);

/** Areas where one report gives high risk, but the consequence puts that report in a lower cell */
export const tooLowForOneReport = <F extends FactorConsequence>(s: RiskMatrixSettings, factors: F[]) =>
  factors.filter(
    (factor) =>
      s.oneReportHighFactorKeys.includes(factor.key) && consequenceOf(s, factor) < oneReportMinConsequence(s.highFrom),
  );

/** A consequence equal to the factor's own is not stored as an override */
export const withConsequence = (s: RiskMatrixSettings, factor: FactorConsequence, value: number): RiskMatrixSettings => {
  const others = Object.fromEntries(Object.entries(s.consequenceOverrides).filter(([key]) => key !== factor.key));
  return {
    ...s,
    consequenceOverrides: value === factor.consequence ? others : { ...others, [factor.key]: value },
  };
};

const LEVEL_FIELDS = ['moderateFrom', 'highFrom', 'deadlineHighDays', 'deadlineModerateDays', 'surveyIntervalMonths'] as const;
const LINK_FIELDS = ['whistleblowingUrl', 'followUpRoutineUrl'] as const;

type NumberField = (typeof LEVEL_FIELDS)[number] | 'combinedAssessmentFrom' | 'minResponses';

const numberChange = (field: NumberField, previous: RiskMatrixSettings, next: RiskMatrixSettings): RiskMatrixChange[] =>
  previous[field] === next[field] ? [] : [{ type: field, from: previous[field], to: next[field] }];

/** In the same order as the form and the API */
export const diffSettings = (
  previous: RiskMatrixSettings,
  next: RiskMatrixSettings,
  factors: RiskMatrixFactor[],
): RiskMatrixChange[] => [
  ...LEVEL_FIELDS.flatMap((field) => numberChange(field, previous, next)),
  ...(previous.likelihoodBands.join() === next.likelihoodBands.join()
    ? []
    : [{ type: 'likelihoodBands' as const, from: previous.likelihoodBands, to: next.likelihoodBands }]),
  ...factors.flatMap((factor): RiskMatrixChange[] => {
    const from = consequenceOf(previous, factor);
    const to = consequenceOf(next, factor);
    return from === to ? [] : [{ type: 'consequence', factorKey: factor.key, from, to }];
  }),
  ...ONE_REPORT_KEYS.flatMap((factorKey): RiskMatrixChange[] => {
    const to = next.oneReportHighFactorKeys.includes(factorKey);
    return previous.oneReportHighFactorKeys.includes(factorKey) === to ? [] : [{ type: 'oneReport', factorKey, to }];
  }),
  ...numberChange('combinedAssessmentFrom', previous, next),
  ...LINK_FIELDS.flatMap((field): RiskMatrixChange[] =>
    (previous[field] ?? '').trim() === (next[field] ?? '').trim() ? [] : [{ type: field }],
  ),
  ...(previous.allowSensitiveSkip === next.allowSensitiveSkip
    ? []
    : [{ type: 'allowSensitiveSkip' as const, to: next.allowSensitiveSkip }]),
  ...numberChange('minResponses', previous, next),
];

/** One line in «Dette er endret» */
export const formatChange = (change: RiskMatrixChange, t: TFunction, factorName: (key: string) => string) => {
  switch (change.type) {
    case 'likelihoodBands':
      return t('hrRiskMatrix.changes.likelihoodBands', { from: change.from.join(', '), to: change.to.join(', ') });
    case 'consequence':
      return t('hrRiskMatrix.changes.consequence', { name: factorName(change.factorKey), from: change.from, to: change.to });
    case 'oneReport':
      return t(change.to ? 'hrRiskMatrix.changes.oneReportOn' : 'hrRiskMatrix.changes.oneReportOff', {
        name: factorName(change.factorKey),
      });
    case 'whistleblowingUrl':
    case 'followUpRoutineUrl':
      return t(`hrRiskMatrix.changes.${change.type}`);
    case 'allowSensitiveSkip':
      return t(change.to ? 'hrRiskMatrix.changes.sensitiveSkipOn' : 'hrRiskMatrix.changes.sensitiveSkipOff');
    default:
      return t(`hrRiskMatrix.changes.${change.type}`, { from: change.from, to: change.to });
  }
};


/** Ids of the fields the error summary links to */
export const FIELD_IDS = {
  moderateFrom: 'hr-rm-moderate',
  band: (index: number) => `hr-rm-band-${index + 1}`,
  consequence: (factorKey: string) => `hr-rm-consequence-${factorKey}`,
  whistleblowingUrl: 'hr-rm-whistleblowing',
  followUpRoutineUrl: 'hr-rm-follow-up',
  sensitiveSkip: 'hr-rm-sensitive-skip',
  minResponses: 'hr-rm-min-responses',
  changeReason: 'hr-rm-change-reason',
  approvalComment: 'hr-rm-approval-comment',
};

export type SendError = { fieldId: string; message: string };

export type SendForm = {
  settings: RiskMatrixSettings;
  factors: Array<Pick<RiskMatrixFactor, 'key' | 'name' | 'consequence'>>;
  changeReason: string;
  mode: HmsApprovalMode;
  comment: string;
  hasMainSafetyRep: boolean;
  mainSafetyRepIsYou?: boolean;
};

export const oneReportConsequenceError = (area: string, s: RiskMatrixSettings, t: TFunction) =>
  t('hrRiskMatrix.consequence.oneReportError', {
    area,
    level: oneReportMinConsequence(s.highFrom),
    min: t(`hms.consequenceLevels.${oneReportMinConsequence(s.highFrom)}`).toLowerCase(),
  });

/** Everything that stops the changes from being sent, in form order */
export const sendErrors = (
  { settings, factors, changeReason, mode, comment, hasMainSafetyRep, mainSafetyRepIsYou }: SendForm,
  t: TFunction,
): SendError[] => {
  const bands = settings.likelihoodBands;
  const firstInvalidBand = Math.max(
    bands.findIndex((_, index) => bandInvalid(bands, index)),
    0,
  );
  const rules: Array<[boolean, SendError]> = [
    [thresholdsInvalid(settings), { fieldId: FIELD_IDS.moderateFrom, message: t('hrRiskMatrix.levels.thresholdsError') }],
    [bandsInvalid(bands), { fieldId: FIELD_IDS.band(firstInvalidBand), message: t('hrRiskMatrix.likelihood.error') }],
    ...tooLowForOneReport(settings, factors).map((factor): [boolean, SendError] => [
      true,
      { fieldId: FIELD_IDS.consequence(factor.key), message: oneReportConsequenceError(factor.name, settings, t) },
    ]),
    [
      Boolean(linkError(settings.whistleblowingUrl)),
      { fieldId: FIELD_IDS.whistleblowingUrl, message: t('hrRiskMatrix.rules.whistleblowingError') },
    ],
    [
      Boolean(linkError(settings.followUpRoutineUrl)),
      { fieldId: FIELD_IDS.followUpRoutineUrl, message: t('hrRiskMatrix.rules.followUpError') },
    ],
    [!changeReason.trim(), { fieldId: FIELD_IDS.changeReason, message: t('hrRiskMatrix.send.reasonError') }],
    [
      approvalCommentMissing(mode, comment, approvalCommentRequired({ hasMainSafetyRep, mainSafetyRepIsYou })),
      { fieldId: FIELD_IDS.approvalComment, message: t('hms.approval.commentError') },
    ],
  ];
  return rules.filter(([failed]) => failed).map(([, error]) => error);
};
